import { Anchor, CloseButton, Flex, Group, Modal, ScrollArea, Stack, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useAnalytics } from "@/analytics";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import type { ChallengeInfo } from "@/sales-tools/challenges/api/getChallenge";
import { useChallenges } from "@/sales-tools/challenges/api/useChallenges";
import { Challenge } from "@/sales-tools/challenges/Challenge";
import { getMetricsDateRange } from "@/sales-tools/leaderboard/utils/getMetricsDateRange";
import { ChecklistSummary } from "@/sales-tools/metrics/checklist-summary/ChecklistSummary";
import { TotalCallCountCard } from "@/sales-tools/metrics/components/TotalCallCountCard";
import { TrophyCaseCard } from "@/sales-tools/metrics/components/TrophyCaseCard";
import { PayDashboard } from "@/sales-tools/pay-dashboard/PayDashboard";
import { useAgentSite, usePartner } from "@/sdk";
import { useAgentOrgChartLocation } from "@/sdk/agent/hooks/useAgentOrgChartLocation";
import { useFeatureFlag } from "@/shared-utils";
import { LeaderboardWidget } from "../../leaderboard/components/LeaderboardWidget";
import { PayWidget } from "../../pay-dashboard/PayWidget";
import styles from "./WidgetContainer.module.css";

export function WidgetContainer() {
    const partner = usePartner();
    const { data: isChallengeEnabledForExpert = false } = useFeatureFlag("Sales-isChallengeEnabled", {
        partner,
    });
    const site = useAgentSite();
    const { data: orgChartSite, isLoading } = useAgentOrgChartLocation({ enabled: !site });
    const agentSite = site ?? orgChartSite;
    const { data: isPayDashboardEnabled } = useFeatureFlag("EWP-isPayDashboardEnabled", {
        partner,
        agentSite,
    });
    const { data: isExpertPerformanceDashboardEnabled } = useFeatureFlag("isExpertPerformanceDashboardEnabled", {
        partner,
        agentSite,
    });
    const { isChallengesEnabled: isChallengesEnabledForPartner } = usePartnerFeatureConfigured();
    const { leaderboards } = usePartnerConfig();
    const [opened, { open, close }] = useDisclosure(false);
    const { dispatcher } = useAnalytics();
    const { data: challengeData } = useChallenges();

    const handleClose = (source: string) => {
        void dispatcher.dispatchBusinessEvent("CloseExpertPerformanceDashboard", { source });
        close();
    };
    const handleOpen = (source: string) => {
        void dispatcher.dispatchBusinessEvent("OpenExpertPerformanceDashboard", { source });
        open();
    };

    function hasEnrollmentChallenge(challenge: ChallengeInfo | null | undefined) {
        if (challenge) {
            return challenge.challengeType === "solo" || challenge.challengeType === "team";
        }
    }

    const hasLeaderboards = leaderboards.length > 0;
    const shouldHideWidgetContainer = !hasLeaderboards && !isChallengeEnabledForExpert && !isPayDashboardEnabled;

    if (shouldHideWidgetContainer) return undefined;

    const showLeftPanel =
        isPayDashboardEnabled ||
        (isChallengeEnabledForExpert && challengeData?.some(hasEnrollmentChallenge)) ||
        hasLeaderboards;

    const showStatsHeader = isExpertPerformanceDashboardEnabled || isPayDashboardEnabled;

    const onClick = () => {
        if (opened) handleClose("PayWidget");
        else if (showStatsHeader) {
            handleOpen("PayWidget");
        }
    };

    if (isLoading) {
        return null;
    }

    return (
        <Flex
            className={opened ? styles.mainOpened : styles.main}
            m="sm"
            bg="dark.8"
            h="calc(100% - (var(--mantine-spacing-sm) * 2))"
        >
            {showLeftPanel && (
                <Stack
                    className={opened ? styles.leftPanelOpened : styles.leftPanel}
                    mr="0"
                    w={220}
                    gap={0}
                    data-testid="widget-container"
                    bg={opened ? "#271a34" : "dark.5"}
                >
                    <ScrollArea type="hover" scrollbarSize={0}>
                        {showStatsHeader && (
                            <Flex className={styles.widgetMain} direction="column" fz="xs" onClick={onClick}>
                                <Flex className={styles.inner}>
                                    <Text fw={700} lh="md">
                                        My stats
                                    </Text>
                                    <UnstyledButton variant="outline">
                                        <Flex align="center">
                                            <Anchor c="primary.1" size="xxs" underline="always">
                                                Show {opened ? "Less" : "More"}
                                            </Anchor>
                                        </Flex>
                                    </UnstyledButton>
                                </Flex>

                                {isPayDashboardEnabled && !isExpertPerformanceDashboardEnabled && <PayWidget />}
                            </Flex>
                        )}

                        {isChallengeEnabledForExpert && isChallengesEnabledForPartner && <Challenge />}
                        {hasLeaderboards && <LeaderboardWidget leaderboards={leaderboards} />}
                    </ScrollArea>
                </Stack>
            )}

            <Modal
                opened={opened}
                withinPortal={false}
                onClose={() => handleClose("PayWidget")}
                withCloseButton={false}
                overlayProps={{
                    backgroundOpacity: 0,
                    blur: 0,
                }}
                transitionProps={{
                    duration: 0,
                }}
                withOverlay={false}
                styles={{
                    content: {
                        backgroundColor: "#14171C",
                        left: 0,
                        position: "absolute",
                        top: 65,
                        bottom: 12,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        boxShadow: "-1px 0 0 0 #8537c5",
                    },
                    body: {
                        padding: 0,
                    },
                }}
            >
                <Stack w="39.5rem" p="md" styles={{ root: { overflow: "auto" } }}>
                    <Stack pos="absolute" right="0.2rem" top=".1rem">
                        <CloseButton variant="transparent" ml="auto" onClick={() => handleClose("CloseButton")} />
                    </Stack>
                    <Flex justify="center">
                        <Stack w="100%">
                            {isExpertPerformanceDashboardEnabled && (
                                <>
                                    <Flex justify="space-between" mt="0.875rem">
                                        <Text size="xxs">Week of {getMetricsDateRange()}</Text>
                                    </Flex>
                                    <Group>
                                        <TotalCallCountCard />
                                    </Group>
                                    <Stack w="24.438rem" pos="absolute" right="0.875rem" top="1.75rem" gap="1.25rem">
                                        <Text size="xxs" c="dark.2" ta="right">
                                            Heads up-this data is not official and might not represent final metrics.
                                        </Text>
                                        <ChecklistSummary />
                                        {isChallengeEnabledForExpert && isChallengesEnabledForPartner && (
                                            <TrophyCaseCard />
                                        )}
                                    </Stack>
                                </>
                            )}
                            {isPayDashboardEnabled && !isExpertPerformanceDashboardEnabled && (
                                <>
                                    <Text fw={700} size="xxs">
                                        Pay Dashboard
                                    </Text>
                                    <PayDashboard />
                                </>
                            )}
                        </Stack>
                    </Flex>
                </Stack>
            </Modal>
        </Flex>
    );
}
